import { AppPage } from "@@/pages/app-page";
import { PageLoad } from "@@/pages/page-load";
import { appPageIdFactory } from "@@/pages/page-navigation/page-navigation-context";
import { useCalculatedProviderStatus } from "@@/providers/calculate-provider-status";
import { useBrandingContext } from "@@/routes/shared/branding-context";
import { Conditional } from "@@/shared/conditional";
import { FlexColumn, FlexRow } from "@@/shared/flex-containers";
import { HeadTitle } from "@@/shared/head-title";
import { usePageBreakpoint } from "@@/shared/responsiveness/use-page-breakpoint";
import { TextBox } from "@@/shared/text";
import { WideScreenWrapper } from "@@/shared/widescreen-wrapper";
import { ColorThemeProvider } from "@@/styles/app-theme-provider";
import type { Interpolation } from "@emotion/react";
import { css, useTheme } from "@emotion/react";
import type { BrandingBackground, Product, Provider } from "@towni/common";
import {
    colorAsString,
    gradientToCssValue,
    isStorageItemImageReference,
    translation,
} from "@towni/common";

import { useIsWithinModal } from "@@/modals/within-modal.context";
import { HoveringProductPageToolbar_Content_Desktop } from "@@/products/shared/product-page-toolbar";
import { InnerProductPage_Accommodation } from "@@/routes/products/$productId/_accommodation/_product_v2_accommodation.page";
import { InnerProductBookablePageV2 } from "@@/routes/products/$productId/_bookable/_product-bookable_v2.page";
import { InnerProductBuyablePageV2 } from "@@/routes/products/$productId/_buyable/_product-buyable_v2.page";
import { GiftCardPageV2 } from "@@/routes/products/$productId/_gift_card/gift-card-page-v2";
import { VoucherPageV2 } from "@@/routes/products/$productId/_voucher/voucher-page-v2";
import { GlobalBackgroundDiv } from "@@/routes/products/$productId/global_background-branding";
import { useGetImageUrl } from "@@/shared/image-url";
import { use100vh } from "@@/shared/use-100-vh";
import type { AppTheme } from "@@/styles/theme";
import { lightColorTheme } from "@@/styles/themes/default.theme";

type Props = {
    product: Product;
    provider: Provider;
};

// type PaletteColor = {
//     red: number;
//     green: number;
//     blue: number;
//     hex: string;
// };

// type Palette = {
//     average_luminance: number;
//     colors: PaletteColor[];
//     dominant_colors: {
//         vibrant: PaletteColor;
//         muted: PaletteColor;
//         vibrant_light: PaletteColor;
//         muted_light: PaletteColor;
//         vibrant_dark: PaletteColor;
//         muted_dark: PaletteColor;
//     };
// };

// const fetchImagePalette = async (imgUrl: string): Promise<Palette> => {
//     // Append the palette extraction parameter to the Imgix URL
//     const paletteUrl = `${imgUrl}?palette=json`;

//     const response = await fetch(paletteUrl);
//     if (!response.ok) {
//         throw new Error("Failed to fetch palette");
//     }

//     const data = await response.json();
//     return data as Palette;
// };

// const useImagePaletteFromImgix = (imgUrl: ImageUrl | undefined) => {
//     return useQuery<Palette>({
//         queryKey: ["palette", imgUrl],
//         queryFn: () => fetchImagePalette(imgUrl ?? ""),
//         enabled: !!imgUrl,
//     });
// };

// const useFirstImagePalette = (product: Product) => {
//     const imageUrl = product.meta.images[0];
//     const getUrl = useImageUrl();
//     return useImagePaletteFromImgix(getUrl(imageUrl));
// };

const productPageMaxWidth = (product: Product) => {
    switch (product.acquirableAs) {
        case "ACCOMMODATION":
        case "BOOKABLE":
        case "GIFTCARD":
            return 1000;

        case "BUYABLE":
        case "VOUCHER":
            return 550;
    }
};

const parseBackgroundCss = (
    backgroundImageCssValue: string | undefined,
    backgroundGradientCssValue: string | undefined,
    backgroundColor: string | undefined,
    cover: "TILE" | "COVER" = "TILE",
    includeFixedAttachment: boolean = false, // New parameter
) => {
    const _backgroundImageCssValue = backgroundImageCssValue;
    const _backgroundGradientCssValue = backgroundGradientCssValue;
    const _backgroundColor = backgroundColor
        ? backgroundColor
        : lightColorTheme.black.light95.asString;

    if (_backgroundImageCssValue) {
        return {
            backgroundColor: _backgroundColor,
            backgroundImage: _backgroundImageCssValue,
            ...{
                backgroundSize: cover === "TILE" ? "auto" : "cover",
                backgroundRepeat: cover === "TILE" ? "repeat" : "no-repeat",
                // Only include fixed attachment if specifically requested
                ...(includeFixedAttachment && {
                    backgroundAttachment: "fixed",
                }),
                backgroundPosition: "top",
            },
        };
    }
    if (_backgroundGradientCssValue && !_backgroundImageCssValue) {
        return {
            backgroundColor: _backgroundColor,
            backgroundImage: _backgroundGradientCssValue,
            ...{
                backgroundSize: cover === "TILE" ? "auto" : "cover",
                backgroundRepeat: cover === "TILE" ? "repeat" : "no-repeat",
                // Only include fixed attachment if specifically requested
                ...(includeFixedAttachment && {
                    backgroundAttachment: "fixed",
                }),
                backgroundPosition: "top",
            },
        };
    }
    return {
        backgroundColor: _backgroundColor,
    };
};

const useFixedBackgroundDiv = (
    background: BrandingBackground | undefined,
): { css: Interpolation<AppTheme>; shouldRender: boolean } => {
    const getImageUrl = useGetImageUrl();

    if (!background) return { css: {}, shouldRender: false };

    const backgroundColor = background.color
        ? colorAsString(background.color)
        : lightColorTheme.white.main.light95.asString;

    const backgroundImageUrl =
        typeof background.image === "string"
            ? background.image
            : isStorageItemImageReference(background?.image)
              ? getImageUrl(background?.image, { imageWidth: "full" })
              : undefined;

    const backgroundImage = backgroundImageUrl?.trim().length
        ? backgroundImageUrl
        : undefined;
    const backgroundImageCssValue = backgroundImage
        ? `url(${backgroundImage})`
        : undefined;
    const backgroundGradientCssValue = background.gradient
        ? gradientToCssValue(background.gradient)
        : undefined;

    // Determine whether to render the fixed background div
    const shouldRenderFixedBackground = !!(
        backgroundImageCssValue || backgroundGradientCssValue
    );

    // Get basic background properties
    const baseBackgroundCss = parseBackgroundCss(
        backgroundImageCssValue,
        backgroundGradientCssValue,
        backgroundColor,
        background.cover,
        false, // Don't use fixed attachment
    );

    // Create fixed background CSS with performance optimizations
    const fixedBackgroundCss = css({
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: -1,
        ...baseBackgroundCss,
        // Add performance optimizations
        transform: "translate3d(0,0,0)",
        willChange: "transform",
        // Safari-specific optimizations
        WebkitBackfaceVisibility: "hidden",
        WebkitPerspective: 1000,
    });

    return {
        css: fixedBackgroundCss,
        shouldRender: shouldRenderFixedBackground,
    };
};

const ProductPageV2 = (props: Props) => {
    const vh = use100vh();
    const theme = useTheme();
    const isWithinModal = useIsWithinModal();

    const isMobile = usePageBreakpoint({ when: "📱" });
    const { product, provider } = props;
    const providerStatus = useCalculatedProviderStatus(provider);
    const { branding, isPending: isBrandingPending } = useBrandingContext();
    const providerIsHidden = providerStatus === "HIDDEN";

    // const backgroundCss = useBrandingBackgroundCss(
    //     branding?.background ?? branding?.product?.peripheralBackground,
    // );
    // const {
    //     css: fixedBackgroundCss,
    //     shouldRender: shouldRenderFixedBackground,
    // } = useFixedBackgroundDiv(
    //     branding?.background ?? branding?.product?.peripheralBackground,
    // );

    if (isBrandingPending) return <PageLoad />;
    if (!branding) throw new Error("No branding and no default pending");

    const maxContentWidth = productPageMaxWidth(props.product);

    return (
        <ColorThemeProvider colorTheme={branding.colorThemes.light}>
            {/* <FixedBackgroundDiv /> */}
            <GlobalBackgroundDiv />
            <AppPage
                pageId={appPageIdFactory(product._id)}
                backgroundColor={theme.colors.transparent.value}
                pageContainerCss={{
                    backgroundColor: theme.colors.transparent.asString,
                }}>
                <HeadTitle title={[product.meta.title, provider.meta.title]} />

                {/* HEADER */}
                <Conditional when={!isMobile && !isWithinModal}>
                    <FlexRow
                        css={{
                            width: "100%",
                            padding: 20,
                            label: "back_button_area",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: theme.colors.white.asString,
                            borderBottom: "1px solid #e5e5e5",
                            zIndex: 9999,
                            position: "sticky",
                            top: 0,
                            alignSelf: "start",
                        }}>
                        <FlexRow
                            css={{
                                paddingLeft: 5,
                                paddingRight: 5,
                                width: "100%",
                                justifyContent: "space-between",
                                alignItems: "center",
                                //maxWidth: maxContentWidth,
                            }}>
                            <HoveringProductPageToolbar_Content_Desktop
                                provider={provider}
                            />
                        </FlexRow>
                    </FlexRow>
                </Conditional>

                {/*  */}

                <WideScreenWrapper
                    css={{
                        backgroundColor: theme.colors.transparent.asString,
                    }}
                    maxContentWidth={maxContentWidth}
                    contentContainerCss={css({
                        backgroundColor: theme.colors.transparent.asString,
                        //  overflowX: "hidden" removed because of sticky stuff,
                        //  overflowY: "hidden" removed because of sticky stuff,,
                        boxShadow: "none",
                        minHeight: isMobile
                            ? `${vh}px`
                            : `calc(${vh}px - 81px)`, //PERFECT!
                    })}>
                    <FlexColumn
                        css={{
                            width: "100%",
                            height: "100%",
                            backgroundColor: "transparent",
                            //      overflowY: "hidden" removed because of sticky stuff,,
                            //      overflowX: "hidden" removed because of sticky stuff,,
                        }}>
                        {/* HIDDEN? */}
                        <Conditional when={providerIsHidden}>
                            <FlexRow fillParentWidth mainAxis="center">
                                <TextBox
                                    text={translation({
                                        sv: "GÖMD",
                                        en: "HIDDEN",
                                    })}
                                    css={{ padding: 20, width: "100%" }}
                                    size={2}
                                    align="center"
                                    weight="900"
                                    backgroundColor={
                                        theme.colors.danger.main.light95
                                    }
                                    color={theme.colors.danger}
                                />
                            </FlexRow>
                        </Conditional>

                        <_InnerProductPage
                            product={product}
                            provider={provider}
                        />
                    </FlexColumn>
                </WideScreenWrapper>
            </AppPage>
        </ColorThemeProvider>
    );
};

const _InnerProductPage = (props: Props) => {
    const { product, provider } = props;

    switch (product.acquirableAs) {
        case "BOOKABLE":
            return (
                <InnerProductBookablePageV2
                    product={product}
                    provider={provider}
                />
            );
        case "BUYABLE":
            return (
                <InnerProductBuyablePageV2
                    provider={provider}
                    product={product}
                    hideLinkToShop={false}
                />
            );
        case "VOUCHER":
            return <VoucherPageV2 voucher={product} provider={provider} />;
        case "GIFTCARD":
            return <GiftCardPageV2 giftCard={product} provider={provider} />;
        case "ACCOMMODATION":
            return (
                <InnerProductPage_Accommodation
                    product={product}
                    provider={provider}
                />
            );
    }
};

export { ProductPageV2 };
